import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard,
  Wrench,
  Users,
  Briefcase,
  ShieldCheck,
  CreditCard,
  MessageSquare,
  Sparkles,
  Star,
  AlertTriangle,
  Bell,
  FileText,
  Search,
  RefreshCw,
  Menu,
  X,
  ShieldAlert,
  MapPin,
  Bot,
  Layers,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminGlobalSearchModal } from '../../components/admin/AdminGlobalSearchModal';
import { AdminOverviewSection } from '../../components/admin/AdminOverviewSection';
import { AdminProfessionalSection } from '../../components/admin/AdminProfessionalSection';
import { AdminCustomerSection } from '../../components/admin/AdminCustomerSection';
import { AdminJobSection } from '../../components/admin/AdminJobSection';
import { AdminVerificationSection } from '../../components/admin/AdminVerificationSection';
import { AdminSubscriptionsSection } from '../../components/admin/AdminSubscriptionsSection';
import { AdminWhatsAppSection } from '../../components/admin/AdminWhatsAppSection';
import { AdminTelegramSection } from '../../components/admin/AdminTelegramSection';
import { AdminAIMonitoringSection } from '../../components/admin/AdminAIMonitoringSection';
import { AdminReviewsSection } from '../../components/admin/AdminReviewsSection';
import { AdminDisputesSection } from '../../components/admin/AdminDisputesSection';
import { AdminBroadcastSection } from '../../components/admin/AdminBroadcastSection';
import { AdminAuditLogSection } from '../../components/admin/AdminAuditLogSection';
import { AdminLocationSection } from '../../components/admin/AdminLocationSection';
import { AdminCategorySection } from '../../components/admin/AdminCategorySection';
import { AdminNotificationSection } from '../../components/admin/AdminNotificationSection';
import { AdminAccessDenied } from '../../components/admin/AdminAccessDenied';

import {
  fetchAdminMetrics,
  fetchAdminUsers,
  fetchAdminJobs,
  fetchAdminVerificationSubmissions,
  fetchAdminSubscriptionsOverview,
  fetchAdminAIHealth,
  fetchAdminReviews,
  fetchAdminDisputes,
  fetchAdminAuditLogs,
  fetchPaystackClientConfig,
  performAdminProfessionalAction,
  performAdminCustomerAction,
  performAdminJobAction,
  performAdminReviewModeration,
  performAdminDisputeResolution,
  sendAdminBroadcast,
} from '../../lib/supabaseService';

import type {
  AdminDashboardMetrics,
  AdminVerificationSubmission,
  AdminAIHealthStats,
  AdminReviewItem,
  AdminDisputeItem,
  AdminAuditLogItem,
  AdminBroadcastPayload,
} from '../../types';

export type AdminTab =
  | 'overview'
  | 'professionals'
  | 'customers'
  | 'jobs'
  | 'categories'
  | 'locations'
  | 'verifications'
  | 'subscriptions'
  | 'notifications'
  | 'whatsapp'
  | 'telegram'
  | 'ai_monitoring'
  | 'reviews'
  | 'disputes'
  | 'broadcast'
  | 'audit_logs';

export const AdminDashboard: React.FC = () => {
  const { currentUser, profiles, jobs: localJobs, quotes, updateProfessionalVerification, viewParams } = useApp();

  // Strict Role Gate Check: Only authenticated Admin can access
  if (!currentUser || currentUser.role !== 'admin') {
    console.warn('[AdminDashboard Diagnostic] Gate rejected unprivileged access. currentUser:', currentUser ? { id: currentUser.id, role: currentUser.role } : 'null');
    return <AdminAccessDenied />;
  }

  useEffect(() => {
    console.info('[AdminDashboard Diagnostic] Admin Dashboard initialized successfully for admin UID:', currentUser.id, 'Email:', currentUser.email);
  }, [currentUser?.id]);

  const getInitialTab = (): AdminTab => {
    if (viewParams?.adminTab) {
      const t = viewParams.adminTab;
      if (t === 'telemetry' || t === 'ai_telemetry' || t === 'ai_monitoring') return 'ai_monitoring';
      return t as AdminTab;
    }
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab') || urlParams.get('adminTab');
      if (tabParam) {
        if (tabParam === 'telemetry' || tabParam === 'ai_telemetry' || tabParam === 'ai_monitoring') return 'ai_monitoring';
        return tabParam as AdminTab;
      }
      const hash = window.location.hash.replace('#', '');
      if (hash === 'telemetry' || hash === 'ai_telemetry' || hash === 'ai_monitoring') return 'ai_monitoring';
      if (window.location.pathname.includes('/telemetry')) return 'ai_monitoring';
    } catch (_) {}
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState<AdminTab>(getInitialTab);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActionProcessing, setIsActionProcessing] = useState(false);

  const handleSelectTab = (tab: AdminTab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tab === 'ai_monitoring' ? 'telemetry' : tab);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {}
  };

  // Real Database State
  const [metrics, setMetrics] = useState<AdminDashboardMetrics | null>(null);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [jobsList, setJobsList] = useState<any[]>([]);
  const [verifications, setVerifications] = useState<AdminVerificationSubmission[]>([]);
  const [subData, setSubData] = useState<{
    metrics: any;
    subscriptions: any[];
    payments: any[];
  } | null>(null);
  const [paystackConfig, setPaystackConfig] = useState<{
    publicKey: string;
    isConfigured: boolean;
    monthlyPriceNaira: number;
  } | null>(null);
  const [aiStats, setAiStats] = useState<AdminAIHealthStats | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<AdminReviewItem[]>([]);
  const [disputes, setDisputes] = useState<AdminDisputeItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<AdminAuditLogItem[]>([]);

  const profilesRef = React.useRef(profiles);
  const localJobsRef = React.useRef(localJobs);
  useEffect(() => {
    profilesRef.current = profiles;
    localJobsRef.current = localJobs;
  }, [profiles, localJobs]);

  // Load all dashboard data from Supabase & API
  const loadDashboardData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [
        metricsRes,
        prosRes,
        custRes,
        jobsRes,
        verifRes,
        subsRes,
        paystackRes,
        aiRes,
        reviewsRes,
        disputesRes,
        auditRes,
      ] = await Promise.allSettled([
        fetchAdminMetrics(),
        fetchAdminUsers('professional'),
        fetchAdminUsers('customer'),
        fetchAdminJobs(),
        fetchAdminVerificationSubmissions(),
        fetchAdminSubscriptionsOverview(),
        fetchPaystackClientConfig(),
        fetchAdminAIHealth(),
        fetchAdminReviews(),
        fetchAdminDisputes(),
        fetchAdminAuditLogs(),
      ]);

      if (metricsRes.status === 'fulfilled' && metricsRes.value?.metrics) {
        setMetrics(metricsRes.value.metrics);
      }
      if (prosRes.status === 'fulfilled' && prosRes.value?.users) {
        setProfessionals(prosRes.value.users);
      } else {
        // Fallback to local profiles
        setProfessionals((profilesRef.current || []).filter((p) => p.role === 'professional'));
      }
      if (custRes.status === 'fulfilled' && custRes.value?.users) {
        setCustomers(custRes.value.users);
      } else {
        setCustomers((profilesRef.current || []).filter((p) => p.role === 'customer'));
      }
      if (
        jobsRes.status === 'fulfilled' &&
        Array.isArray(jobsRes.value?.jobs) &&
        jobsRes.value.jobs.length > 0
      ) {
        setJobsList(jobsRes.value.jobs);
      } else if (localJobsRef.current && localJobsRef.current.length > 0) {
        setJobsList(localJobsRef.current);
      } else if (jobsRes.status === 'fulfilled' && Array.isArray(jobsRes.value?.jobs)) {
        setJobsList(jobsRes.value.jobs);
      }
      if (verifRes.status === 'fulfilled' && verifRes.value?.submissions) {
        setVerifications(verifRes.value.submissions);
      }
      if (subsRes.status === 'fulfilled' && subsRes.value) {
        setSubData(subsRes.value);
      }
      if (paystackRes.status === 'fulfilled' && paystackRes.value) {
        setPaystackConfig(paystackRes.value);
      }
      if (aiRes.status === 'fulfilled' && aiRes.value?.stats) {
        setAiStats(aiRes.value.stats);
        setAiError(null);
      } else {
        const errorMsg =
          aiRes.status === 'fulfilled'
            ? aiRes.value?.error
            : 'Unable to connect to Gemini AI telemetry service.';
        setAiError(errorMsg || 'Unable to load Gemini AI telemetry. Please try again.');
      }
      if (reviewsRes.status === 'fulfilled' && reviewsRes.value?.reviews) {
        setReviews(reviewsRes.value.reviews);
      }
      if (disputesRes.status === 'fulfilled' && disputesRes.value?.disputes) {
        setDisputes(disputesRes.value.disputes);
      }
      if (auditRes.status === 'fulfilled' && auditRes.value?.logs) {
        setAuditLogs(auditRes.value.logs);
      }
    } catch (err) {
      console.error('Error fetching admin dashboard data:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handle Artisan Actions (Verify, Reject, Suspend, Reactivate, Resubmit)
  const handleProfessionalAction = async (
    professionalId: string,
    action: 'verify' | 'reject' | 'suspend' | 'reactivate' | 'resubmit',
    reason?: string
  ) => {
    setIsActionProcessing(true);
    try {
      const serverAction = action === 'resubmit' ? 'request_resubmission' : action;
      const res = await performAdminProfessionalAction(professionalId, serverAction, reason);
      if (!res.success) {
        console.error('Professional action failed on server:', res.error);
        throw new Error(res.error || `Failed to ${action} professional.`);
      }
      if (action === 'verify' || action === 'reject' || action === 'resubmit') {
        updateProfessionalVerification(
          professionalId,
          action === 'verify' ? 'verified' : action === 'reject' ? 'rejected' : 'resubmission'
        );
      }
      await loadDashboardData();
    } catch (err: any) {
      console.error(`Error performing ${action} on professional:`, err);
      throw err;
    } finally {
      setIsActionProcessing(false);
    }
  };

  // Handle Customer Actions (Suspend, Reactivate)
  const handleCustomerAction = async (
    customerId: string,
    action: 'suspend' | 'reactivate',
    reason?: string
  ) => {
    setIsActionProcessing(true);
    try {
      await performAdminCustomerAction(customerId, action, reason);
      await loadDashboardData();
    } catch (err) {
      console.error(`Error performing ${action} on customer:`, err);
    } finally {
      setIsActionProcessing(false);
    }
  };

  // Handle Job Actions (Flag, Unflag, Cancel, Status update, Assign, Reassign, Unassign)
  const handleJobAction = async (
    jobId: string,
    action: 'flag' | 'unflag' | 'cancel' | 'update_status' | 'assign' | 'reassign' | 'unassign',
    reason?: string,
    adminNotes?: string,
    newStatus?: string,
    professionalId?: string
  ) => {
    setIsActionProcessing(true);
    try {
      await performAdminJobAction(jobId, action, reason, adminNotes, newStatus, professionalId);
      await loadDashboardData();
    } catch (err) {
      console.error(`Error performing ${action} on job:`, err);
    } finally {
      setIsActionProcessing(false);
    }
  };

  // Handle Review Moderation (Hide, Restore, Flag)
  const handleReviewModeration = async (
    reviewId: string,
    action: 'hide' | 'restore' | 'flag',
    reason?: string
  ) => {
    setIsActionProcessing(true);
    try {
      await performAdminReviewModeration(reviewId, action, reason);
      await loadDashboardData();
    } catch (err) {
      console.error(`Error moderating review:`, err);
    } finally {
      setIsActionProcessing(false);
    }
  };

  // Handle Dispute Resolution
  const handleDisputeResolution = async (
    disputeId: string,
    status: 'investigating' | 'resolved' | 'dismissed',
    resolutionNotes: string
  ) => {
    setIsActionProcessing(true);
    try {
      await performAdminDisputeResolution(disputeId, status, resolutionNotes);
      await loadDashboardData();
    } catch (err) {
      console.error(`Error resolving dispute:`, err);
    } finally {
      setIsActionProcessing(false);
    }
  };

  // Handle Broadcast Dispatch
  const handleBroadcast = async (payload: AdminBroadcastPayload) => {
    setIsActionProcessing(true);
    try {
      await sendAdminBroadcast(payload);
      await loadDashboardData();
    } catch (err) {
      console.error(`Error sending broadcast:`, err);
    } finally {
      setIsActionProcessing(false);
    }
  };

  const navItems: {
    id: AdminTab;
    label: string;
    icon: React.ReactNode;
    badge?: number | string;
    badgeColor?: string;
  }[] = [
    {
      id: 'overview',
      label: 'Executive Overview',
      icon: <LayoutDashboard className="w-4 h-4" />,
    },
    {
      id: 'verifications',
      label: 'Verification Review Centre',
      icon: <ShieldCheck className="w-4 h-4" />,
      badge: verifications.filter((v) => v.verificationStatus === 'pending').length || undefined,
      badgeColor: 'bg-amber-500 text-slate-950 font-bold',
    },
    {
      id: 'professionals',
      label: 'Professionals',
      icon: <Wrench className="w-4 h-4" />,
      badge: professionals.length,
    },
    {
      id: 'customers',
      label: 'Customers',
      icon: <Users className="w-4 h-4" />,
      badge: customers.length,
    },
    {
      id: 'jobs',
      label: 'Jobs & Matching',
      icon: <Briefcase className="w-4 h-4" />,
      badge: jobsList.filter((j) => j.status === 'open').length || undefined,
      badgeColor: 'bg-emerald-500/20 text-emerald-300',
    },
    {
      id: 'categories',
      label: 'Trades & Categories',
      icon: <Layers className="w-4 h-4" />,
      badge: '24 Trades',
      badgeColor: 'bg-teal-500/20 text-teal-300',
    },
    {
      id: 'locations',
      label: 'Locations (774 LGAs)',
      icon: <MapPin className="w-4 h-4" />,
      badge: '36+FCT',
      badgeColor: 'bg-emerald-500/20 text-emerald-300',
    },
    {
      id: 'subscriptions',
      label: 'Financials & Commission',
      icon: <CreditCard className="w-4 h-4" />,
    },
    {
      id: 'notifications',
      label: 'Email & SMS Center',
      icon: <Bell className="w-4 h-4" />,
      badge: 'Live',
      badgeColor: 'bg-emerald-500/20 text-emerald-300',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp AI Centre',
      icon: <MessageSquare className="w-4 h-4" />,
    },
    {
      id: 'telegram',
      label: 'Telegram Operations',
      icon: <Bot className="w-4 h-4" />,
      badge: 'Bot',
      badgeColor: 'bg-blue-500/20 text-blue-300',
    },
    {
      id: 'ai_monitoring',
      label: 'Gemini AI Telemetry',
      icon: <Sparkles className="w-4 h-4" />,
    },
    {
      id: 'reviews',
      label: 'Reviews Moderation',
      icon: <Star className="w-4 h-4" />,
    },
    {
      id: 'disputes',
      label: 'Dispute Resolution Desk',
      icon: <AlertTriangle className="w-4 h-4" />,
      badge: disputes.filter((d) => d.status === 'open').length || undefined,
      badgeColor: 'bg-rose-500 text-white font-bold',
    },
    {
      id: 'broadcast',
      label: 'Broadcast Alerts',
      icon: <Bell className="w-4 h-4" />,
    },
    {
      id: 'audit_logs',
      label: 'Security Audit Logs',
      icon: <FileText className="w-4 h-4" />,
    },
  ];

  const pendingCount = verifications.filter((v) => v.verificationStatus === 'pending').length;
  const openDisputesCount = disputes.filter((d) => d.status === 'open').length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans antialiased">
      {/* Top Header */}
      <AdminHeader
        activeTab={activeTab}
        onRefresh={loadDashboardData}
        isRefreshing={isRefreshing}
        onOpenSearch={() => setIsSearchOpen(true)}
        pendingVerificationsCount={pendingCount}
        openDisputesCount={openDisputesCount}
      />

      {/* Global Universal Search Modal */}
      <AdminGlobalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectEntity={(type, id, entity) => {
          if (type === 'professionals') setActiveTab('professionals');
          else if (type === 'customers') setActiveTab('customers');
          else if (type === 'jobs') setActiveTab('jobs');
          else if (type === 'subscriptions') setActiveTab('subscriptions');
          else if (type === 'disputes') setActiveTab('disputes');
        }}
      />

      {/* Mobile Navigation Toggle */}
      <div className="lg:hidden bg-slate-900 border-b border-slate-800 px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold text-emerald-400 capitalize">
          Current: {activeTab.replace('_', ' ')}
        </span>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-1.5 bg-slate-800 text-slate-300 rounded-lg flex items-center gap-1 text-xs"
        >
          {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          <span>Menu</span>
        </button>
      </div>

      {/* Main Workspace Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          {/* Left Navigation Sidebar */}
          <aside
            className={`lg:col-span-1 bg-slate-900 border border-slate-800 rounded-3xl p-3 space-y-1 shadow-xl sticky top-6 ${
              isMobileMenuOpen ? 'block' : 'hidden lg:block'
            }`}
          >
            <div className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
              Operations & Desks
            </div>

            <nav className="space-y-1">
              {navItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelectTab(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-2xl text-xs font-semibold transition-all group text-left ${
                      isActive
                        ? 'bg-emerald-500 text-slate-950 font-bold shadow-md'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <span className={isActive ? 'text-slate-950' : 'text-slate-400 group-hover:text-emerald-400'}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.label}</span>
                    </div>

                    {item.badge !== undefined && (
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono flex-shrink-0 ml-1 ${
                          isActive
                            ? 'bg-slate-950 text-white font-bold'
                            : item.badgeColor || 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Right Main Content Area */}
          <main className="lg:col-span-4 min-h-[75vh]">
            {activeTab === 'overview' && (
              <AdminOverviewSection
                metrics={metrics}
                onNavigateTab={(tab) => handleSelectTab(tab)}
                pendingPros={verifications.filter((v) => v.verificationStatus === 'pending')}
                disputes={disputes.filter((d) => d.status === 'open' || d.status === 'investigating')}
                recentJobs={jobsList}
              />
            )}

            {activeTab === 'verifications' && (
              <AdminVerificationSection
                verifications={verifications}
                onDecision={async (proId, decision, reason) => {
                  await handleProfessionalAction(proId, decision, reason);
                }}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'professionals' && (
              <AdminProfessionalSection
                professionals={professionals}
                onAction={handleProfessionalAction}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'customers' && (
              <AdminCustomerSection
                customers={customers}
                onAction={handleCustomerAction}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'jobs' && (
              <AdminJobSection
                jobs={jobsList}
                professionals={professionals}
                onAction={handleJobAction}
                isProcessing={isActionProcessing}
                onRefresh={loadDashboardData}
              />
            )}

            {activeTab === 'categories' && <AdminCategorySection />}

            {activeTab === 'locations' && (
              <AdminLocationSection
                professionals={professionals}
                jobs={jobsList}
              />
            )}

            {activeTab === 'subscriptions' && (
              <AdminSubscriptionsSection
                subData={subData}
                paystackConfig={paystackConfig}
                onRefresh={loadDashboardData}
                isLoading={isRefreshing}
              />
            )}

            {activeTab === 'notifications' && <AdminNotificationSection />}

            {activeTab === 'whatsapp' && <AdminWhatsAppSection />}

            {activeTab === 'telegram' && <AdminTelegramSection />}

            {activeTab === 'ai_monitoring' && (
              <AdminAIMonitoringSection
                aiStats={aiStats}
                onRefresh={loadDashboardData}
                isLoading={isRefreshing}
                error={aiError}
              />
            )}

            {activeTab === 'reviews' && (
              <AdminReviewsSection
                reviews={reviews}
                onModerate={handleReviewModeration}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'disputes' && (
              <AdminDisputesSection
                disputes={disputes}
                onResolve={handleDisputeResolution}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'broadcast' && (
              <AdminBroadcastSection
                onSendBroadcast={handleBroadcast}
                isProcessing={isActionProcessing}
              />
            )}

            {activeTab === 'audit_logs' && (
              <AdminAuditLogSection
                logs={auditLogs}
                onRefresh={loadDashboardData}
                isLoading={isRefreshing}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
